import { Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { ProfitByProductRowDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

type ProfitByProductTableProps = {
  rows?: ProfitByProductRowDTO[];
  isLoading?: boolean;
};

function marginClass(margin: string): string {
  const n = Number(margin);
  if (n > 0) return "text-leaf";
  if (n < 0) return "text-destructive";
  return "text-muted-foreground";
}

export function ProfitByProductTable({
  rows = [],
  isLoading,
}: ProfitByProductTableProps) {
  return (
    <div className="brand-card overflow-hidden">
      <div className="border-secondary/15 border-b px-4 py-3 sm:px-5">
        <h2 className="font-display text-lg">ກຳໄລຕາມສິນຄ້າ</h2>
        <p className="text-muted-foreground text-sm">
          ລາຍຮັບ, COGS ແລະ margin ຕໍ່ variant
        </p>
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={`profit-row-${i}`} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
      ) : (
        <>
          <div className="flex flex-col gap-3 p-4 md:hidden">
            {rows.map((row) => (
              <ReportMobileCard key={row.variantId}>
                <ReportProductLink
                  productId={row.productId}
                  productName={row.productName}
                  variantName={row.variantName}
                />
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <ReportStat
                    label="ຂາຍ"
                    value={formatQtyWithUnit(row.quantity, row.stockUnitLabel)}
                  />
                  <ReportStat label="ລາຍຮັບ" value={formatLak(row.revenue)} />
                  <ReportStat
                    label="COGS"
                    value={formatLak(row.cogs)}
                    valueClassName="text-muted-foreground"
                  />
                  <ReportStat
                    label="ກຳໄລ"
                    value={formatLak(row.grossProfit)}
                    valueClassName={marginClass(row.grossMarginPercent)}
                  />
                  <ReportStat
                    label="Margin"
                    value={`${row.grossMarginPercent}%`}
                    valueClassName={marginClass(row.grossMarginPercent)}
                    className="col-span-2"
                  />
                </dl>
              </ReportMobileCard>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-secondary/10 border-b text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium sm:px-5">ສິນຄ້າ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຂາຍ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ລາຍຮັບ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    COGS
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ກຳໄລ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    Margin
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.variantId}
                    className="border-secondary/5 border-b last:border-0"
                  >
                    <td className="px-4 py-2.5 sm:px-5">
                      <ReportProductLink
                        productId={row.productId}
                        productName={row.productName}
                        variantName={row.variantName}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {formatQtyWithUnit(row.quantity, row.stockUnitLabel)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {formatLak(row.revenue)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground tabular-nums sm:px-5">
                      {formatLak(row.cogs)}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right font-medium tabular-nums sm:px-5 ${marginClass(row.grossMarginPercent)}`}
                    >
                      {formatLak(row.grossProfit)}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right tabular-nums sm:px-5 ${marginClass(row.grossMarginPercent)}`}
                    >
                      {row.grossMarginPercent}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
